/**
 * 추천인 모달 — 내 코드 확인/복사, 코드 입력, 추천 현황, 등급 보상 청구.
 * CITY 기능과 완전히 독립적으로 구성되어 있습니다(공유 상태 없음).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { IoClose, IoGiftOutline, IoCopyOutline, IoCheckmarkCircle, IoHelpCircleOutline } from 'react-icons/io5';
import { useAppContext } from '../../contexts/AppContext';
import { getEnvironment } from '../../utils/environment';
import {
  claimReferralReward,
  getCachedReferralStatus,
  getReferralStatus,
  registerReferral
} from '../../services/referralApi';
import { REFERRAL_KEYS } from './constants';

const ReferralModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { piUser, setSpotPoints } = useAppContext();
  const environment = getEnvironment();
  const userId = piUser?.uid;

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [copyDone, setCopyDone] = useState(false);
  const [inputCode, setInputCode] = useState('');
  const [registering, setRegistering] = useState(false);
  const [registerMsg, setRegisterMsg] = useState(null);
  const [registerErr, setRegisterErr] = useState(null);
  const [claimingTier, setClaimingTier] = useState(null);
  const [claimErr, setClaimErr] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const load = useCallback(async ({ forceRefresh = false } = {}) => {
    if (!userId) return;
    const cached = forceRefresh ? null : getCachedReferralStatus(userId, environment);
    if (cached && cached.signupRewarded !== undefined) {
      setStatus(cached);
      setLoading(false);
      return;
    }
    if (cached) setStatus(cached);
    setLoading(!cached);
    setLoadError(null);
    try {
      const s = await getReferralStatus(userId, environment, { forceRefresh });
      setStatus(s);
      if (s.spotPoints !== undefined) setSpotPoints(s.spotPoints);
    } catch {
      setLoadError(REFERRAL_KEYS.ERROR_LOAD);
    } finally {
      setLoading(false);
    }
  }, [userId, environment, setSpotPoints]);

  useEffect(() => {
    if (isOpen && userId) load();
  }, [isOpen, userId, load]);

  if (!isOpen) return null;

  const handleClose = () => {
    setInputCode('');
    setRegisterMsg(null);
    setRegisterErr(null);
    setClaimErr(null);
    setShowHelp(false);
    onClose();
  };

  const handleCopy = () => {
    if (!status?.code) return;
    navigator.clipboard?.writeText(status.code).then(() => {
      setCopyDone(true);
      setTimeout(() => setCopyDone(false), 1800);
    }).catch(() => {});
  };

  const getRegisterErrorMessage = (e) => {
    const code = (e && (e.code || e.message)) || '';
    if (code === 'INVALID_CODE') return t(REFERRAL_KEYS.ERROR_INVALID_CODE);
    if (code === 'SELF_REFERRAL' || code === 'SELF_REFERRAL_NOT_ALLOWED') return t(REFERRAL_KEYS.ERROR_SELF_REFERRAL);
    if (code === 'ACCOUNT_TOO_OLD' || code === 'REGISTER_WINDOW_EXPIRED') {
      return t(REFERRAL_KEYS.ERROR_ACCOUNT_TOO_OLD);
    }
    if (code === 'MUTUAL_REFERRAL') return t(REFERRAL_KEYS.ERROR_MUTUAL);
    if (code === 'REFERRER_DAILY_LIMIT') return t(REFERRAL_KEYS.ERROR_DAILY_LIMIT);
    if (code === 'REWARD_DAILY_CAP') return t(REFERRAL_KEYS.ERROR_DAILY_CAP);
    if (code === 'REWARDS_DISABLED') return t(REFERRAL_KEYS.ERROR_REWARDS_OFF);
    if (code === 'ALREADY_REGISTERED') return t(REFERRAL_KEYS.INPUT_ALREADY_REGISTERED);
    return t(REFERRAL_KEYS.ERROR_GENERIC);
  };

  const handleRegister = async () => {
    if (!userId || !inputCode.trim() || registering) return;
    setRegistering(true);
    setRegisterErr(null);
    setRegisterMsg(null);
    try {
      const result = await registerReferral(userId, inputCode.trim().toUpperCase(), environment);
      if (result.spotPoints !== undefined) setSpotPoints(result.spotPoints);
      if (result.deferred && result.code === 'REWARD_DAILY_CAP') {
        setRegisterMsg(t(REFERRAL_KEYS.ERROR_DAILY_CAP));
      } else if (result.deferred && result.code === 'REWARDS_DISABLED') {
        setRegisterMsg(t(REFERRAL_KEYS.ERROR_REWARDS_OFF));
      } else {
        setRegisterMsg(t(REFERRAL_KEYS.INPUT_SUCCESS));
      }
      setInputCode('');
      await load({ forceRefresh: true });
    } catch (e) {
      const code = (e && (e.code || e.message)) || '';
      if (code === 'ALREADY_REGISTERED') {
        await load({ forceRefresh: true });
      }
      setRegisterErr(getRegisterErrorMessage(e));
    } finally {
      setRegistering(false);
    }
  };

  const handleClaim = async (tier) => {
    if (!userId || claimingTier) return;
    setClaimingTier(tier);
    setClaimErr(null);
    try {
      const result = await claimReferralReward(userId, tier, environment);
      if (result.rewardType === 'SPOT' && result.spotPoints !== undefined) {
        setSpotPoints(result.spotPoints);
      }
      await load({ forceRefresh: true });
    } catch (e) {
      setClaimErr(t(REFERRAL_KEYS.ERROR_GENERIC));
    } finally {
      setClaimingTier(null);
    }
  };

  const tiers = status?.tiers || [];
  const referralCount = status?.referralCount ?? ((status?.activatedCount ?? 0) + (status?.pendingCount ?? 0));
  const qualifiedCount = status?.qualifiedCount ?? status?.activatedCount ?? referralCount;
  const claimedTiers = status?.claimedTiers || [];
  const alreadyReferred = !!status?.referredBy;
  const showSkeleton = loading && !status;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="referral-modal-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)',
        padding: 16,
        boxSizing: 'border-box',
      }}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          maxWidth: 400,
          width: '100%',
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottom: '1px solid #eee', flexShrink: 0 }}>
          <h2 id="referral-modal-title" style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 6 }}>
            <IoGiftOutline size={20} color="#f59e0b" />
            {t(REFERRAL_KEYS.MODAL_TITLE)}
            <button
              type="button"
              onClick={() => setShowHelp((open) => !open)}
              aria-label={t(REFERRAL_KEYS.HELP_LABEL)}
              aria-expanded={showHelp}
              style={{
                background: 'none', border: 'none', padding: 0, display: 'flex',
                cursor: 'pointer', color: '#9CA3AF'
              }}
            >
              <IoHelpCircleOutline size={18} />
            </button>
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label={t(REFERRAL_KEYS.MODAL_CLOSE)}
            style={{
              background: 'rgba(0,0,0,0.06)', border: 'none', borderRadius: 8,
              width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#374151'
            }}
          >
            <IoClose size={20} />
          </button>
        </div>

        <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
          {!userId && (
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#6B7280', lineHeight: 1.5 }}>
              {t(REFERRAL_KEYS.LOGIN_TO_USE)}
            </p>
          )}

          {userId && loadError && (
            <p style={{ margin: '0 0 12px', color: '#dc2626', fontSize: 13 }}>{t(loadError)}</p>
          )}

          {userId && showSkeleton && (
            <div aria-label={t(REFERRAL_KEYS.MY_CODE_LOADING)} aria-busy="true">
              {[88, 118, 92].map((height, index) => (
                <div
                  key={height}
                  style={{
                    height,
                    marginBottom: index === 2 ? 0 : 12,
                    borderRadius: 12,
                    background: 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)',
                    backgroundSize: '200% 100%'
                  }}
                />
              ))}
            </div>
          )}

          {showHelp && (
            <div style={{
              marginBottom: 10, padding: '8px 10px', borderRadius: 10,
              background: '#F9FAFB', color: '#4B5563', fontSize: 12, lineHeight: 1.45,
              whiteSpace: 'pre-line'
            }}>
              {t(REFERRAL_KEYS.HELP_BODY)}
            </div>
          )}

          {userId && !showSkeleton && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{
                  flex: 1, fontSize: 16, fontWeight: 800, letterSpacing: 1.5, color: '#1a1a1a',
                  background: '#fff', border: '1px dashed #f59e0b', borderRadius: 10,
                  padding: '8px 10px', textAlign: 'center'
                }}>
                  {loading ? t(REFERRAL_KEYS.MY_CODE_LOADING) : (status?.code || '—')}
                </div>
                <button
                  type="button"
                  onClick={handleCopy}
                  disabled={!status?.code}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    background: copyDone ? '#059669' : '#f59e0b', color: '#fff',
                    border: 'none', borderRadius: 10, padding: '8px 12px',
                    fontSize: 13, fontWeight: 700, cursor: status?.code ? 'pointer' : 'default',
                    opacity: status?.code ? 1 : 0.5
                  }}
                >
                  {copyDone ? <IoCheckmarkCircle size={16} /> : <IoCopyOutline size={16} />}
                  {copyDone ? t(REFERRAL_KEYS.COPY_DONE) : t(REFERRAL_KEYS.COPY_BTN)}
                </button>
              </div>

              {!alreadyReferred ? (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="text"
                      value={inputCode}
                      onChange={(e) => setInputCode(e.target.value.toUpperCase())}
                      placeholder={t(REFERRAL_KEYS.INPUT_PLACEHOLDER)}
                      maxLength={12}
                      style={{
                        flex: 1, fontSize: 14, letterSpacing: 1.5, padding: '8px 10px',
                        border: '1px solid #d1d5db', borderRadius: 10, textTransform: 'uppercase'
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleRegister}
                      disabled={!inputCode.trim() || registering}
                      style={{
                        background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10,
                        padding: '8px 14px', fontSize: 13, fontWeight: 700,
                        cursor: !inputCode.trim() || registering ? 'default' : 'pointer',
                        opacity: !inputCode.trim() || registering ? 0.6 : 1
                      }}
                    >
                      {t(REFERRAL_KEYS.INPUT_SUBMIT)}
                    </button>
                  </div>
                  <p style={{ margin: '6px 0 0', color: '#B45309', fontSize: 12, fontWeight: 700 }}>
                    {t(REFERRAL_KEYS.SIGNUP_REWARD_HINT)}
                  </p>
                  {registerMsg && <p style={{ margin: '6px 0 0', color: '#059669', fontSize: 12, fontWeight: 600 }}>{registerMsg}</p>}
                  {registerErr && <p style={{ margin: '6px 0 0', color: '#dc2626', fontSize: 12 }}>{registerErr}</p>}
                </div>
              ) : (
                <p style={{ margin: '0 0 8px', color: '#059669', fontSize: 13, fontWeight: 700 }}>
                  {t(REFERRAL_KEYS.REGISTERED_SHORT)} · {t(REFERRAL_KEYS.SIGNUP_REWARD_HINT)}
                </p>
              )}

              <div style={{ fontSize: 13, fontWeight: 800, color: '#111827', marginBottom: 6 }}>
                {t(REFERRAL_KEYS.COUNT_LABEL, { count: referralCount })}
              </div>

              <div>
                {tiers.map((tierDef) => {
                  const claimed = claimedTiers.includes(tierDef.tier);
                  const reached = qualifiedCount >= tierDef.required;
                  return (
                    <div key={tierDef.tier} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 8, padding: '7px 0', borderTop: '1px solid #F3F4F6'
                    }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: reached ? '#111827' : '#9CA3AF' }}>
                        {t(REFERRAL_KEYS.TIER_PROGRESS, { count: Math.min(qualifiedCount, tierDef.required), required: tierDef.required })}
                        {' · '}
                        {tierDef.amount} {t(REFERRAL_KEYS.TIER_REWARD_SPOT)}
                      </span>
                      {claimed ? (
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#059669' }}>{t(REFERRAL_KEYS.TIER_CLAIMED)}</span>
                      ) : reached ? (
                        <button
                          type="button"
                          onClick={() => handleClaim(tierDef.tier)}
                          disabled={claimingTier === tierDef.tier}
                          style={{
                            border: 'none', borderRadius: 8, padding: '6px 10px',
                            fontSize: 12, fontWeight: 700, cursor: 'pointer',
                            background: '#f59e0b', color: '#fff'
                          }}
                        >
                          {t(REFERRAL_KEYS.TIER_CLAIM_BTN)}
                        </button>
                      ) : null}
                    </div>
                  );
                })}
                {claimErr && <p style={{ margin: '8px 0 0', color: '#dc2626', fontSize: 12 }}>{claimErr}</p>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralModal;
